const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const formatNumber = (element, value) => {
  const decimals = Number(element.dataset.decimals || 0);
  const locale = document.documentElement.lang || 'hu';
  const number = value.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${element.dataset.prefix || ''}${number}${element.dataset.suffix || ''}`;
};

const countUp = (element) => {
  const target = Number(element.dataset.mszCount);
  if (Number.isNaN(target)) return;
  if (prefersReducedMotion()) {
    element.textContent = formatNumber(element, target);
    return;
  }
  const start = performance.now();
  const duration = 1800;
  const step = (now) => {
    const progress = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - progress, 3);
    element.textContent = formatNumber(element, target * eased);
    if (progress < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
};

const revealObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const element = /** @type {HTMLElement} */ (entry.target);
      revealObserver.unobserve(element);

      if (element.dataset.mszCount != null) countUp(element);
      if (element.dataset.mszBar != null) element.style.width = `${element.dataset.mszBar}%`;
      if (element.dataset.mszReveal != null) element.classList.add('is-revealed');
    }
  },
  { threshold: 0.12, rootMargin: '0px 0px -30px 0px' }
);

const animationsEnabled = () => document.documentElement.classList.contains('msz-reveal-ready');

const scanRevealTargets = (root = document) => {
  root.querySelectorAll('[data-msz-reveal]:not([data-msz-observed])').forEach((element) => {
    element.setAttribute('data-msz-observed', '');
    if (!animationsEnabled()) {
      element.classList.add('is-revealed');
      return;
    }
    if (element.dataset.mszReveal === 'stagger') {
      Array.from(element.children).forEach((child, index) => {
        /** @type {HTMLElement} */ (child).style.setProperty('--msz-delay', `${index * 90}ms`);
      });
    }
    revealObserver.observe(element);
  });

  root.querySelectorAll('[data-msz-count]:not([data-msz-observed])').forEach((element) => {
    element.setAttribute('data-msz-observed', '');
    if (!animationsEnabled()) return;
    element.textContent = formatNumber(element, 0);
    revealObserver.observe(element);
  });

  root.querySelectorAll('[data-msz-bar]:not([data-msz-observed])').forEach((element) => {
    element.setAttribute('data-msz-observed', '');
    if (!animationsEnabled()) {
      /** @type {HTMLElement} */ (element).style.width = `${element.dataset.mszBar}%`;
      return;
    }
    revealObserver.observe(element);
  });
};

const allowAutoplay = () => !prefersReducedMotion() && !navigator.connection?.saveData;

const videoObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      const video = /** @type {HTMLVideoElement} */ (entry.target);
      if (entry.isIntersecting) {
        if (!video.dataset.loaded) {
          video.querySelectorAll('source[data-src]').forEach((source) => {
            /** @type {HTMLSourceElement} */ (source).src = /** @type {HTMLSourceElement} */ (source).dataset.src || '';
          });
          video.dataset.loaded = 'true';
          video.load();
        }
        video
          .play()
          .then(() => video.setAttribute('data-playing', ''))
          .catch(() => {});
      } else if (!video.paused) {
        video.pause();
      }
    }
  },
  { rootMargin: '200px 0px' }
);

const scanVideos = (root = document) => {
  if (!allowAutoplay()) return;
  root.querySelectorAll('video[data-msz-lazy-video]:not([data-msz-observed])').forEach((video) => {
    video.setAttribute('data-msz-observed', '');
    videoObserver.observe(video);
  });
};

window.mszThemeReady = true;
scanRevealTargets();
scanVideos();
document.addEventListener('shopify:section:load', (event) => {
  scanRevealTargets(/** @type {HTMLElement} */ (event.target));
  scanVideos(/** @type {HTMLElement} */ (event.target));
});
document.addEventListener('shopify:block:select', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  target.querySelectorAll('[data-msz-reveal]').forEach((element) => element.classList.add('is-revealed'));
  if (target.matches('[data-msz-reveal]')) target.classList.add('is-revealed');
});

class MszGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-msz-gallery-track]');
    this.slides = this.track ? Array.from(this.track.children) : [];
    this.counter = this.querySelector('[data-msz-gallery-counter]');
    this.dots = Array.from(this.querySelectorAll('[data-msz-gallery-dot]'));
    this.thumbStrip = this.querySelector('[data-msz-gallery-thumbs]');
    this.index = 0;
    if (!this.track || this.slides.length === 0) return;

    this.querySelector('[data-msz-gallery-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-msz-gallery-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.go(index)));

    this.onScroll = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        const width = this.track.clientWidth || 1;
        const index = Math.round(this.track.scrollLeft / width);
        if (index !== this.index) this.update(index);
      });
    };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.update(0);
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
  }

  go(index) {
    const count = this.slides.length;
    const next = (index + count) % count;
    this.track.scrollTo({
      left: next * this.track.clientWidth,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  update(index) {
    const changed = index !== this.index;
    this.index = index;
    if (this.counter) this.counter.textContent = `${index + 1} / ${this.slides.length}`;
    this.dots.forEach((dot, dotIndex) => {
      dot.toggleAttribute('aria-current', dotIndex === index);
    });
    this.slides.forEach((slide, slideIndex) => slide.toggleAttribute('inert', slideIndex !== index));

    const thumb = this.dots[index];
    if (changed && this.thumbStrip && thumb) {
      const strip = this.thumbStrip;
      const offset = thumb.getBoundingClientRect().left - strip.getBoundingClientRect().left;
      const target = strip.scrollLeft + offset - (strip.clientWidth - thumb.clientWidth) / 2;
      strip.scrollTo({ left: Math.max(0, target), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }
  }
}

class MszBundlePicker extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.variantInput = this.querySelector('input[name="id"]');
    this.quantityInput = this.querySelector('input[name="quantity"]');
    this.addEventListener('change', this.onChange);
    if (this.form?.closest('[data-msz-direct-checkout]')) {
      this.form.addEventListener('submit', this.onDirectSubmit);
      window.addEventListener('pageshow', this.resetBusy);
    }
    let checked = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-msz-bundle-input]:checked'));
    if (!checked) {
      checked = this.querySelector('[data-msz-bundle-input]');
      if (checked) checked.checked = true;
    }
    if (checked) this.apply(checked);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.form?.removeEventListener('submit', this.onDirectSubmit);
    window.removeEventListener('pageshow', this.resetBusy);
  }

  onDirectSubmit = (event) => {
    if (this.submitting) {
      event.preventDefault();
      return;
    }
    this.submitting = true;
    this.querySelectorAll('[data-msz-atc]').forEach((button) => button.setAttribute('aria-busy', 'true'));
  };

  resetBusy = () => {
    this.submitting = false;
    this.querySelectorAll('[data-msz-atc]').forEach((button) => button.removeAttribute('aria-busy'));
  };

  onChange = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (input.matches('[data-msz-bundle-input]')) this.apply(input);
  };

  apply(input) {
    const { variantId, quantity, priceLabel, saveShort, label, available } = input.dataset;
    if (this.variantInput && variantId) this.variantInput.value = variantId;
    if (this.quantityInput && quantity) this.quantityInput.value = quantity;

    this.querySelectorAll('[data-msz-selected-price]').forEach((element) => (element.textContent = priceLabel || ''));
    this.querySelectorAll('[data-msz-selected-save]').forEach((element) => {
      element.textContent = saveShort || '';
      element.hidden = !saveShort;
    });
    this.querySelectorAll('[data-msz-selected-label]').forEach((element) => (element.textContent = label || ''));
    this.querySelectorAll('[data-msz-sticky-chip]').forEach((chip) => {
      chip.setAttribute('aria-checked', String(chip.dataset.value === input.value));
    });

    const soldOut = available === 'false';
    this.querySelectorAll('[data-msz-atc]').forEach((button) => {
      /** @type {HTMLButtonElement} */ (button).disabled = soldOut;
    });
  }
}

class MszStickyBar extends HTMLElement {
  connectedCallback() {
    const picker = this.closest('msz-bundle-picker');
    this.buildOptions(picker);
    this.anchor = picker?.querySelector('[data-msz-sticky-anchor]');
    this.mainButton = picker?.querySelector('[data-msz-atc-main]');
    this.querySelector('[data-msz-sticky-button]')?.addEventListener('click', this.onClick);
    if (!this.anchor) return;

    this.observer = new IntersectionObserver(([entry]) => {
      const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.toggleAttribute('data-visible', passed);
      this.toggleAttribute('inert', !passed);
    });
    this.toggleAttribute('inert', true);
    this.observer.observe(this.anchor);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  onClick = () => {
    /** @type {HTMLButtonElement | null | undefined} */ (this.mainButton)?.click();
  };

  buildOptions(picker) {
    const container = this.querySelector('[data-msz-sticky-options]');
    if (!container || !picker) return;
    const inputs = Array.from(picker.querySelectorAll('[data-msz-bundle-input]'));
    container.replaceChildren();
    container.style.setProperty('--msz-chip-count', String(Math.max(1, inputs.length)));
    if (inputs.length < 2) {
      container.hidden = true;
      return;
    }
    inputs.forEach((input) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'msz-sticky-bar__chip';
      chip.setAttribute('role', 'radio');
      chip.setAttribute('aria-checked', String(input.checked));
      chip.dataset.mszStickyChip = '';
      chip.dataset.value = input.value;

      const label = document.createElement('span');
      label.className = 'msz-sticky-bar__chip-label';
      label.textContent = input.dataset.label || '';
      const price = document.createElement('span');
      price.className = 'msz-sticky-bar__chip-price';
      price.textContent = input.dataset.priceLabel || '';
      chip.append(label, price);

      if (input.dataset.compareLabel) {
        const compare = document.createElement('s');
        compare.className = 'msz-sticky-bar__chip-compare';
        compare.textContent = input.dataset.compareLabel;
        chip.append(compare);
      }
      if (input.dataset.saveShort) {
        const save = document.createElement('span');
        save.className = 'msz-sticky-bar__chip-save';
        save.textContent = input.dataset.saveShort;
        chip.append(save);
      }
      if (input.dataset.available === 'false') chip.disabled = true;

      chip.addEventListener('click', () => {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      container.append(chip);
    });
  }
}

class MszCountdown extends HTMLElement {
  connectedCallback() {
    this.parts = {
      hours: this.querySelector('[data-msz-cd="h"]'),
      minutes: this.querySelector('[data-msz-cd="m"]'),
      seconds: this.querySelector('[data-msz-cd="s"]'),
    };
    this.tick();
    this.timer = window.setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  getEnd() {
    const fixed = Date.parse(this.dataset.end || '');
    if (this.dataset.mode === 'fixed' && !Number.isNaN(fixed)) return fixed;

    const duration = Number(this.dataset.duration || 0) * 1000;
    const key = `msz-countdown-${this.dataset.key || 'default'}`;
    const now = Date.now();
    let end = 0;
    try {
      end = Number(window.localStorage.getItem(key));
    } catch (error) {
      end = this.memoryEnd || 0;
    }
    if (!end || end < now) {
      end = now + duration;
      this.memoryEnd = end;
      try {
        window.localStorage.setItem(key, String(end));
      } catch (error) {}
    }
    return end;
  }

  tick() {
    const remaining = Math.max(0, Math.floor((this.getEnd() - Date.now()) / 1000));
    const pad = (value) => String(value).padStart(2, '0');
    const hours = Math.min(99, Math.floor(remaining / 3600));
    if (this.parts.hours) this.parts.hours.textContent = pad(hours);
    if (this.parts.minutes) this.parts.minutes.textContent = pad(Math.floor((remaining % 3600) / 60));
    if (this.parts.seconds) this.parts.seconds.textContent = pad(remaining % 60);
  }
}

class MszLoadMore extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-msz-load-more]');
    this.button?.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
  }

  onClick = () => {
    const hidden = Array.from(this.querySelectorAll('[data-msz-hidden-item]'));
    hidden.forEach((item) => {
      item.removeAttribute('hidden');
      item.removeAttribute('data-msz-hidden-item');
    });
    scanRevealTargets(this);
    /** @type {HTMLElement | null} */ (hidden[0]?.querySelector('a, button, [tabindex]'))?.focus?.();
    this.button?.parentElement?.remove();
  };
}

class MszToast extends HTMLElement {
  connectedCallback() {
    const picker = this.closest('msz-bundle-picker');
    if (!picker) return;
    import('@shopify/events')
      .then(({ StandardEvents }) => {
        this.eventName = StandardEvents.cartLinesUpdate;
        picker.addEventListener(this.eventName, this.onCartUpdate);
      })
      .catch(() => {});
    this.picker = picker;
  }

  disconnectedCallback() {
    if (this.eventName) this.picker?.removeEventListener(this.eventName, this.onCartUpdate);
    window.clearTimeout(this.timer);
  }

  onCartUpdate = (event) => {
    event.promise
      ?.then(({ detail } = {}) => {
        if (detail?.didError) return;
        this.toggleAttribute('data-visible', true);
        window.clearTimeout(this.timer);
        this.timer = window.setTimeout(() => this.removeAttribute('data-visible'), 2200);
      })
      .catch(() => {});
  };
}

document.addEventListener('change', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  if (target instanceof HTMLSelectElement && target.matches('[data-msz-autosubmit]')) target.form?.submit();
});

const define = (name, constructor) => {
  if (!customElements.get(name)) customElements.define(name, constructor);
};

define('msz-gallery', MszGallery);
define('msz-bundle-picker', MszBundlePicker);
define('msz-sticky-bar', MszStickyBar);
define('msz-countdown', MszCountdown);
define('msz-load-more', MszLoadMore);
define('msz-toast', MszToast);
